import { useQuery, useQueries } from '@tanstack/react-query';
import { shelfMapService } from '@/services/shelfMapService';
import type {
  ShelfMapLayout,
  ShelfMapLayoutWithSlots,
  ShelfMapCompositionPayload,
  ShelfMapInventorySearchResponse,
} from '@/services/shelfMapService';
import { useAuth } from '@/hooks/useAuth';
import { httpClient } from '@/utils/httpClient';
import { API_CONFIG } from '@/config/api';

// ---------------------------------------------------------------------------
// Root location type (warehouse/site selector)
// ---------------------------------------------------------------------------

export interface RootLocation {
  id: string;
  display_name: string;
  entity_identifier: string;
  entity_data: { template_id?: string | null } | null;
}

export interface LocationTemplate {
  id: string;
  display_name: string;
  entity_identifier: string;
}

/**
 * Fetches all root locations (structural_type = 'root') from the
 * location_hierarchy view. These represent warehouse/site boundaries and are
 * the valid targets for shelf_map_layout.warehouse_entity_id.
 */
export const useRootLocations = () => {
  const { isAuthenticated } = useAuth();
  return useQuery<RootLocation[]>({
    queryKey: ['location-hierarchy', 'root'],
    queryFn: () =>
      httpClient.get<RootLocation[]>(
        `${API_CONFIG.ENDPOINTS.LOCATION_HIERARCHY}?structural_type=eq.root&select=id,display_name,entity_identifier,entity_data&order=display_name.asc`,
      ),
    enabled: isAuthenticated,
    staleTime: 60_000,
  });
};

/**
 * Fetches all location_template entities. Used to resolve template names
 * from the template_id stored on root location entity_data.
 */
export const useLocationTemplates = () => {
  const { isAuthenticated } = useAuth();
  return useQuery<LocationTemplate[]>({
    queryKey: ['entities', 'location_template'],
    queryFn: () =>
      httpClient.get<LocationTemplate[]>(
        `${API_CONFIG.ENDPOINTS.ENTITIES}?entity_type=eq.location_template&is_current=eq.true&select=id,display_name,entity_identifier&order=display_name.asc`,
      ),
    enabled: isAuthenticated,
    staleTime: 300_000,
  });
};

// ---------------------------------------------------------------------------
// Warehouse resolution for a single location (Issue #1084)
// ---------------------------------------------------------------------------

export interface LocationWarehouseInfo {
  /** Root (warehouse) entity ID that the given location belongs to. */
  warehouseEntityId: string | null;
  /** Display name of the root (warehouse) location. */
  warehouseName: string | null;
}

interface LocationHierarchyBreadcrumbRow {
  breadcrumb_ids: string[];
  breadcrumb_names: string[];
}

/**
 * Resolves the warehouse (root location) that a given location entity
 * belongs to. location_hierarchy.breadcrumb_ids[0] / breadcrumb_names[0]
 * is always the root/warehouse node for any location in the tree.
 *
 * Used so callers (e.g. Catalog item detail) do not need to know the
 * location hierarchy shape to launch a warehouse-scoped shelf map.
 */
export const useLocationWarehouse = (locationId: string | null | undefined) => {
  const { isAuthenticated } = useAuth();
  const trimmedId = locationId?.trim() || null;

  const query = useQuery<LocationHierarchyBreadcrumbRow[]>({
    queryKey: ['location-hierarchy', 'warehouse-for-location', trimmedId],
    queryFn: () =>
      httpClient.get<LocationHierarchyBreadcrumbRow[]>(
        `${API_CONFIG.ENDPOINTS.LOCATION_HIERARCHY}?id=eq.${trimmedId}&select=breadcrumb_ids,breadcrumb_names`,
      ),
    enabled: isAuthenticated && !!trimmedId,
    staleTime: 60_000,
  });

  const row = query.data?.[0];
  const data: LocationWarehouseInfo | undefined = trimmedId
    ? {
        warehouseEntityId: row?.breadcrumb_ids?.[0] ?? null,
        warehouseName: row?.breadcrumb_names?.[0] ?? null,
      }
    : undefined;

  return { data, isLoading: query.isLoading, isError: query.isError };
};

// ---------------------------------------------------------------------------
// Query key factory
// ---------------------------------------------------------------------------

export const shelfMapQueryKeys = {
  all: ['shelf-map'] as const,
  layouts: () => [...shelfMapQueryKeys.all, 'layouts'] as const,
  layoutList: (warehouseId?: string) => [...shelfMapQueryKeys.layouts(), { warehouseId }] as const,
  layout: (layoutId: string) => [...shelfMapQueryKeys.all, 'layout', layoutId] as const,
  inventory: (layoutId: string, locationId: string) =>
    [...shelfMapQueryKeys.all, 'inventory', layoutId, locationId] as const,
  composition: (layoutId: string) => [...shelfMapQueryKeys.all, 'composition', layoutId] as const,
  inventorySearch: (layoutId: string, query: string) =>
    [...shelfMapQueryKeys.all, 'inventory-search', layoutId, query] as const,
  contextSearch: (layoutId: string, linsChunk: string[]) =>
    [...shelfMapQueryKeys.all, 'context-search', layoutId, linsChunk] as const,
};

// ---------------------------------------------------------------------------
// List layouts
// ---------------------------------------------------------------------------

export const useShelfMapLayouts = (warehouseEntityId?: string) => {
  const { isAuthenticated } = useAuth();
  return useQuery<ShelfMapLayout[]>({
    queryKey: shelfMapQueryKeys.layoutList(warehouseEntityId),
    queryFn: () => shelfMapService.listLayouts(warehouseEntityId),
    enabled: isAuthenticated,
    staleTime: 30_000,
  });
};

// ---------------------------------------------------------------------------
// Get single layout with slots
// ---------------------------------------------------------------------------

export const useShelfMapLayout = (layoutId: string | null) => {
  const { isAuthenticated } = useAuth();
  return useQuery<ShelfMapLayoutWithSlots>({
    queryKey: shelfMapQueryKeys.layout(layoutId ?? ''),
    queryFn: () => shelfMapService.getLayout(layoutId!),
    enabled: isAuthenticated && !!layoutId,
    staleTime: 30_000,
  });
};

// ---------------------------------------------------------------------------
// Location inventory (selected-location drawer)
// ---------------------------------------------------------------------------

export const useShelfMapLocationInventory = (
  layoutId: string | null,
  locationId: string | null,
) => {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: shelfMapQueryKeys.inventory(layoutId ?? '', locationId ?? ''),
    queryFn: () => shelfMapService.getLocationInventory(layoutId!, locationId!),
    enabled: isAuthenticated && !!layoutId && !!locationId,
    staleTime: 60_000,
    // Limit retries so transient server errors (e.g. PGRST202 schema cache
    // miss) fail fast rather than blocking the drawer for ~30 s.
    retry: 1,
    // Do not keep error responses in cache — force a fresh fetch on next open.
    gcTime: 0,
  });
};

// ---------------------------------------------------------------------------
// Composition (Issue #224)
// ---------------------------------------------------------------------------

/**
 * Fetches the full template+location composition for a shelf-map layout.
 * Composition path: layout → warehouse → template → location_types → locations.
 *
 * Returns:
 *   - data.template_id / data.template_name – the bound template (or null)
 *   - data.location_types – ordered array of location types in the template
 *   - data.locations – ordered array of locations within the template context
 *
 * Empty arrays are returned when no template binding exists; the query succeeds.
 * The query is disabled when layoutId is null.
 */
export const useShelfMapComposition = (layoutId: string | null) => {
  const { isAuthenticated } = useAuth();
  return useQuery<ShelfMapCompositionPayload>({
    queryKey: shelfMapQueryKeys.composition(layoutId ?? ''),
    queryFn: () => shelfMapService.getComposition(layoutId!),
    enabled: isAuthenticated && !!layoutId,
    staleTime: 30_000,
  });
};

/**
 * Searches all slots in a layout for on-hand inventory matching the given
 * LIN or nomenclature query. Disabled when layoutId or query is empty.
 */
export const useShelfMapInventorySearch = (layoutId: string | null, query: string) => {
  const { isAuthenticated } = useAuth();
  const trimmed = query.trim();
  return useQuery<ShelfMapInventorySearchResponse>({
    queryKey: shelfMapQueryKeys.inventorySearch(layoutId ?? '', trimmed),
    queryFn: () => shelfMapService.searchInventory(layoutId!, trimmed),
    enabled: isAuthenticated && !!layoutId && trimmed.length >= 2,
    staleTime: 30_000,
  });
};

// ---------------------------------------------------------------------------
// Hand-receipt-scoped context locations (Issue #912, bulk lookup #1130)
// ---------------------------------------------------------------------------

export interface ShelfMapContextLocationsResult {
  /** Location IDs within the layout that stock on-hand inventory matching any of the given LINs. */
  locationIds: Set<string>;
  isLoading: boolean;
  /** True only when every chunk failed — no context could be resolved at all. */
  isError: boolean;
  /** True when some (but not all) chunks failed — the result is incomplete, not confirmed-empty. */
  isPartial: boolean;
  /** LINs that could not be resolved because their chunk's lookup failed. */
  failedLins: string[];
}

/** Bounded batch size per request so a receipt with many LINs never fires an unbounded fan-out. */
const CONTEXT_SEARCH_CHUNK_SIZE = 50;

const chunkLins = (lins: string[], size: number): string[][] => {
  const chunks: string[][] = [];
  for (let i = 0; i < lins.length; i += size) {
    chunks.push(lins.slice(i, i + size));
  }
  return chunks;
};

/**
 * Resolves the set of location_entity_ids within a layout that currently
 * stock on-hand inventory matching any of the given catalog LINs.
 *
 * Calls the bulk exact-match context-search endpoint in bounded chunks
 * (rather than one request per LIN) so a receipt with many distinct LINs
 * cannot fan out into an unbounded number of requests. Failures are
 * surfaced via `isError` (all chunks failed) / `isPartial` (some chunks
 * failed) instead of being silently dropped, which previously produced a
 * false "no items stock this receipt" empty state (#1130).
 *
 * Used to scope the Front Window shelf map viewer to a loaded Temporary
 * Hand Receipt's context so the header totals and slot highlighting reflect
 * only receipt-relevant locations instead of the full site. Issue #912.
 */
export const useShelfMapContextLocations = (
  layoutId: string | null,
  lins: string[],
): ShelfMapContextLocationsResult => {
  const { isAuthenticated } = useAuth();
  const uniqueLins = Array.from(
    new Set(lins.map((lin) => lin.trim()).filter((lin) => lin.length > 0)),
  );
  const linChunks = chunkLins(uniqueLins, CONTEXT_SEARCH_CHUNK_SIZE);

  const results = useQueries({
    queries: linChunks.map((linsChunk) => ({
      queryKey: shelfMapQueryKeys.contextSearch(layoutId ?? '', linsChunk),
      queryFn: () => shelfMapService.searchInventoryByLins(layoutId!, linsChunk),
      enabled: isAuthenticated && !!layoutId,
      staleTime: 30_000,
    })),
  });

  const locationIds = new Set<string>();
  const failedLins: string[] = [];
  let failedChunks = 0;

  results.forEach((result, index) => {
    if (result.data) {
      for (const match of result.data.matches) {
        locationIds.add(match.location_entity_id);
      }
      if (result.data.failed_lins.length > 0) {
        failedLins.push(...result.data.failed_lins);
        failedChunks += 1;
      }
    } else if (result.isError) {
      failedLins.push(...(linChunks[index] ?? []));
      failedChunks += 1;
    }
  });

  const totalChunks = linChunks.length;
  const isError = totalChunks > 0 && failedChunks === totalChunks;
  const isPartial = failedChunks > 0 && !isError;

  return {
    locationIds,
    isLoading: totalChunks > 0 && results.some((r) => r.isLoading),
    isError,
    isPartial,
    failedLins,
  };
};
